import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { editUserNote } from '../../../redux/Users/actions';

function EditUserNoteDialog({ isOpen, onClose, userData, note, onNoteEdited }) {
  const dispatch = useDispatch();

  const [noteBody, setNoteBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Prefill with the existing note text whenever a different note is opened.
  useEffect(() => {
    if (isOpen && note) {
      setNoteBody(note.body || '');
      setSubmitError(null);
    }
  }, [isOpen, note]);

  const trimmedBody = noteBody.trim();
  const isUnchanged = note ? trimmedBody === (note.body || '').trim() : true;
  const canSubmit = Boolean(trimmedBody) && !isUnchanged;

  function handleClose() {
    onClose();
    // Delay the reset so the text doesn't visibly clear while the dialog closes.
    setTimeout(() => {
      setNoteBody('');
      setSubmitError(null);
    }, 300);
  }

  async function handleSubmit() {
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await editUserNote({
        userId: userData.id,
        noteId: note.id,
        body: trimmedBody,
      })(dispatch);

      if (result.success) {
        if (onNoteEdited) {
          await onNoteEdited();
        }
        handleClose();
      } else {
        setSubmitError('Could not save changes. Please try again.');
      }
    } catch (error) {
      setSubmitError('Could not save changes. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} sx={{ userSelect: 'none' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Edit Note</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack width="420px" maxWidth="100%" paddingTop="8px">
          <TextField
            label="Note"
            placeholder="e.g. CP One Time Excep Cash 48HRS ND 5/05/2026"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            multiline
            rows={5}
            autoFocus
            sx={{ width: '100% !important' }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Box sx={{ width: '100%' }}>
          {submitError && (
            <Alert severity="error" sx={{ marginBottom: '12px' }}>
              {submitError}
            </Alert>
          )}
          <Button
            type="button"
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditUserNoteDialog;
